import {
  FormContext,
  FormInputGroupLayoutContext,
  FormState,
  optimizedRetryOption,
  Select,
  SelectProps,
  uniqueGUID,
} from "o4a-react";
import * as React from "react";
import { NormalError } from "savant-connector";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { ttlOneMinCaching } from "../../constants/uiConstants";
import { VmdbDbVersionSummary, VmdbDbVersionSummaryCollection } from "../../gen/clients/mchub-azure-api-client-vmdb";
import { IdResourceType } from "../../helpers/idHelper";
import { responseItemstoArray } from "../../helpers/resourceHelper";
import { useQueryCall } from "../../hooks/useQueryCall";
import { getOciRegion } from "../../utils";

export interface VmdbDbVersionProps extends Omit<SelectProps, "options"> {
  shape?: string;
  subscriptionId: string;
  location: string;
  onError?: (error: NormalError) => void;
  onMissingDependencies?: (missingDependencies: IdResourceType[]) => void;
  isPdbSupported?:(supportsPdb:boolean | undefined) => void;
}

export const VMDbVersionSelect = (
  {
    defaultValue,
    fieldName,
    label,
    testId,
    shape,
    placeholder,
    required,
    subField,
    tooltip,
    subscriptionId,
    location,
    validator,
    onChange,
    onError,
    onMissingDependencies,
    isPdbSupported,
  }:VmdbDbVersionProps,
): JSX.Element => {
  const [key, setKey] = React.useState<string>(uniqueGUID());

  const { groupName } = React.useContext(
    FormInputGroupLayoutContext,
  );

  const form: FormState = React.useContext(FormContext);

  const { loading, response, error } = useQueryCall({
    wait: !(subscriptionId && location),
    method: apiClients.withRegion(getOciRegion(location)).vmDatabaseApi.listVmdbDbVersions,
    options: {
      args: { subscriptionId, dbSystemShape: shape },
      caching: ttlOneMinCaching,
      fetchAllPages: true,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadDbVersion(),
      },
    },
  });
  const items = (response
    && responseItemstoArray<VmdbDbVersionSummaryCollection, VmdbDbVersionSummary>(response)) || [];
  const options = items?.map(item => ({
    id: item.version,
    text: item.version,
  }));

  React.useEffect(() => {
    form.setValue(undefined, fieldName, groupName);
    setKey(uniqueGUID());

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shape, subscriptionId, location]);

  React.useEffect(() => {
    setKey(uniqueGUID());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  React.useEffect(() => {
    if (error) {
      onError?.(error);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const internalOnClick = (): void => {
    const missingDependencies: IdResourceType[] = [];
    if (!location) missingDependencies.push(IdResourceType.LOCATION);
    if (missingDependencies.length > 0) {
      onMissingDependencies?.(missingDependencies);
    }
  };

  const derivedPlaceholder = !loading && !error && options.length === 0
    ? Messages.createCommon.loadingNone(label?.toLowerCase() || "")
    : placeholder;

  const onDbVersionChange = (versionId: string) : void => {
    const selectedVersion = items?.find(version => (version.version === versionId));
    onChange?.(versionId);
    isPdbSupported?.(selectedVersion?.supportsPdb);
  };

  return (
    <Select
      key={key}
      testId={testId}
      loading={!!location && loading}
      required={required}
      subField={subField}
      fieldName={fieldName}
      label={label}
      options={options}
      placeholder={derivedPlaceholder}
      defaultValue={defaultValue}
      tooltip={tooltip}
      onClick={internalOnClick}
      onChange={onDbVersionChange}
      validator={validator}
    />
  );
};
